import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";

export interface MyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onButtonClick: () => void;
  id: string;
  showAlert: (message: string, severity?: "success" | "info" | "warning" | "error") => void;
  showLoading: () => void;
  hideLoading: () => void;
}

type BankOption = {
  id: number;
  name: string;
};

type CollectDiscountSettings = {
  days_discount?: number | string;
  enable_discount_manual?: boolean | number | string;
};

type PayDetail = {
  id: number;
  value: number | string;
  value_discount: number | string;
  balance: number | string;
  cash: boolean;
  receipt_bank?: string | null;
  bank?: {
    id: number;
    name: string;
  } | null;
  lend?: {
    id: number;
    balance: number | string;
    daily_pay_value: number | string;
    date?: string | null;
    value_utility?: number | string;
    collect?: CollectDiscountSettings | null;
    client?: {
      fullname: string;
      idnumber: string;
    } | null;
  } | null;
};

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90vw",
  maxWidth: 520,
  maxHeight: "85vh",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 2,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const parseAmount = (value: string | number | null | undefined) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const normalizedValue = String(value ?? "").trim().replace(",", ".");
  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const normalizeDiscountDays = (value: unknown) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : 0;
};

const getLendAgeDays = (lendDateValue: unknown) => {
  if (!lendDateValue) return null;

  const parsedDate = new Date(String(lendDateValue));
  if (Number.isNaN(parsedDate.getTime())) return null;

  const today = new Date();
  const utcStart = Date.UTC(
    parsedDate.getFullYear(),
    parsedDate.getMonth(),
    parsedDate.getDate(),
  );
  const utcToday = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  return Math.max(0, Math.floor((utcToday - utcStart) / 86400000));
};

const getCoordinates = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve(`${position.coords.latitude}, ${position.coords.longitude}`);
      },
      (error) => reject(error),
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    );
  });
};

const MyModal: React.FC<MyModalProps> = ({
  isOpen,
  onClose,
  onButtonClick,
  id,
  showAlert,
  showLoading,
  hideLoading,
}) => {
  const [payDetail, setPayDetail] = useState<PayDetail | null>(null);
  const [banks, setBanks] = useState<BankOption[]>([]);
  const [userId, setUserId] = useState<string>("");
  const [location, setLocation] = useState<string | null>(null);
  const [paymentValue, setPaymentValue] = useState("0");
  const [discountValue, setDiscountValue] = useState("0");
  const [selectedFormPay, setSelectedFormPay] = useState("1");
  const [selectedBank, setSelectedBank] = useState("");
  const [receiptNumber, setReceiptNumber] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [discountEnabled, setDiscountEnabled] = useState(false);

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        maximumFractionDigits: 0,
      }),
    []
  );

  const originalValue = parseAmount(payDetail?.value);
  const originalDiscount = parseAmount(payDetail?.value_discount);
  const currentLoanBalance = parseAmount(payDetail?.lend?.balance);
  const baseBalance = currentLoanBalance + originalValue + originalDiscount;
  const collectSettings = payDetail?.lend?.collect ?? null;
  const discountDays = normalizeDiscountDays(collectSettings?.days_discount);
  const enableDiscountManual = Boolean(
    Number(collectSettings?.enable_discount_manual ?? 1),
  );
  const lendAgeDays = getLendAgeDays(payDetail?.lend?.date);
  const utilityValue = Math.max(0, parseAmount(payDetail?.lend?.value_utility));
  const canDiscount =
    lendAgeDays !== null &&
    lendAgeDays <= discountDays &&
    utilityValue > 0;
  const discountAmount = discountEnabled ? Math.max(0, utilityValue * 0.5) : 0;
  const newValue = parseAmount(paymentValue);
  const manualDiscountRaw = enableDiscountManual
    ? Math.min(Math.max(0, parseAmount(discountValue)), baseBalance)
    : 0;
  const appliedDiscount = enableDiscountManual
    ? Math.min(manualDiscountRaw, Math.max(0, baseBalance - newValue))
    : discountEnabled && canDiscount
      ? discountAmount
      : 0;
  const newTotal = newValue + appliedDiscount;
  const projectedBalance = Math.max(0, baseBalance - newTotal);
  const balanceDifference = projectedBalance - currentLoanBalance;
  const maxAbono = Math.max(0, baseBalance - discountAmount);

  const resetState = () => {
    setPayDetail(null);
    setPaymentValue("0");
    setDiscountValue("0");
    setSelectedFormPay("1");
    setSelectedBank("");
    setReceiptNumber("");
    setErrorMessage("");
    setIsSaving(false);
    setDiscountEnabled(false);
  };

  useEffect(() => {
    if (!isOpen || !id) {
      return;
    }

    let isMounted = true;

    const loadData = async () => {
      try {
        showLoading();
        const [payResponse, banksResponse, userResponse] = await Promise.all([
          axios.get(`pays/${id}`),
          axios.get("/banks"),
          axios.get("user"),
        ]);

        if (!isMounted) {
          return;
        }

        const nextPay: PayDetail = payResponse.data;
        const nextEnableDiscountManual = Boolean(
          Number(nextPay.lend?.collect?.enable_discount_manual ?? 1),
        );
        const nextDiscountValue = String(parseAmount(nextPay.value_discount));

        setPayDetail(nextPay);
        setBanks(banksResponse.data?.data ?? []);
        setUserId(String(userResponse.data?.data?.id ?? ""));
        setPaymentValue(String(parseAmount(nextPay.value)));
        setDiscountValue(nextDiscountValue);
        setSelectedFormPay(nextPay.cash ? "1" : "0");
        setSelectedBank(nextPay.bank?.id ? String(nextPay.bank.id) : "");
        setReceiptNumber(nextPay.receipt_bank ?? "");
        setDiscountEnabled(!nextEnableDiscountManual && parseAmount(nextPay.value_discount) > 0);
        setErrorMessage("");
      } catch (error) {
        console.error("Error loading pay data:", error);
        showAlert("No se pudo cargar la información del abono.", "error");
      } finally {
        hideLoading();
      }

      try {
        const coords = await getCoordinates();
        if (isMounted) {
          setLocation(coords);
        }
      } catch (error) {
        if (isMounted) {
          setLocation(null);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [id, isOpen, hideLoading, showAlert, showLoading]);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!canDiscount && discountEnabled && !enableDiscountManual) {
      setDiscountEnabled(false);
      setDiscountValue("0");
    }
  }, [canDiscount, discountEnabled, enableDiscountManual]);

  useEffect(() => {
    if (enableDiscountManual) {
      return;
    }

    if (discountEnabled && canDiscount) {
      setPaymentValue(String(maxAbono));
      setDiscountValue(String(discountAmount));
    }
  }, [canDiscount, discountAmount, discountEnabled, enableDiscountManual, maxAbono]);

  useEffect(() => {
    if (!payDetail) {
      return;
    }

    if (newTotal <= 0) {
      setErrorMessage("El total del abono debe ser mayor que cero.");
      return;
    }

    if (newTotal > baseBalance) {
      setErrorMessage("La suma del abono y el descuento no puede superar el saldo.");
      return;
    }

    setErrorMessage("");
  }, [baseBalance, newTotal, payDetail]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handlePaymentValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    const abono = parseAmount(nextValue);
    const discountForLimit = enableDiscountManual
      ? Math.min(Math.max(0, parseAmount(discountValue)), baseBalance)
      : discountEnabled && canDiscount
        ? discountAmount
        : 0;
    const maxValue = Math.max(0, baseBalance - discountForLimit);

    if (abono > maxValue) {
      setErrorMessage(
        enableDiscountManual
          ? "La suma del abono y el descuento no puede superar el saldo."
          : "El valor del abono no puede exceder el saldo disponible.",
      );
      setPaymentValue(String(maxValue));
      return;
    }

    setErrorMessage("");
    setPaymentValue(nextValue);
  };

  const handleDiscountValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!enableDiscountManual) {
      return;
    }

    let nextDiscount = Math.min(
      Math.max(0, parseAmount(event.target.value)),
      baseBalance,
    );
    const abono = parseAmount(paymentValue);

    if (abono + nextDiscount > baseBalance) {
      nextDiscount = Math.max(0, baseBalance - abono);
      setErrorMessage("La suma del abono y el descuento no puede superar el saldo.");
    } else {
      setErrorMessage("");
    }

    setDiscountValue(String(nextDiscount));
  };

  const handleFormPayChange = (event: SelectChangeEvent<string>) => {
    setSelectedFormPay(event.target.value);
    if (event.target.value === "1") {
      setSelectedBank("");
      setReceiptNumber("");
    }
  };

  const getBalanceImpactLabel = () => {
    if (balanceDifference < 0) {
      return `El saldo disminuye ${currencyFormatter.format(Math.abs(balanceDifference))}`;
    }

    if (balanceDifference > 0) {
      return `El saldo aumenta ${currencyFormatter.format(Math.abs(balanceDifference))}`;
    }

    return "El saldo no cambia";
  };

  const handleSubmit = async () => {
    if (!payDetail) {
      return;
    }

    if (selectedFormPay === "0" && (!selectedBank || !receiptNumber.trim())) {
      setErrorMessage("Debe seleccionar un banco y registrar el número de recibo.");
      return;
    }

    if (newTotal <= 0 || newTotal > baseBalance) {
      return;
    }

    let currentLocation = location;
    if (!currentLocation) {
      try {
        currentLocation = await getCoordinates();
        setLocation(currentLocation);
      } catch (error) {
        currentLocation = null;
        showAlert("No se pudieron obtener las coordenadas. Se actualizará sin ubicación.", "warning");
      }
    }

    const formData: Record<string, unknown> = {
      value: newValue,
      value_discount: appliedDiscount,
      cash: selectedFormPay === "1",
      bank_id: selectedFormPay === "0" ? Number(selectedBank) : null,
      receipt_bank: selectedFormPay === "0" ? receiptNumber.trim() : null,
      location: currentLocation,
      state: true,
    };

    const parsedUserId = Number(userId);
    if (Number.isFinite(parsedUserId) && parsedUserId > 0) {
      formData.user_id = parsedUserId;
    }

    try {
      setIsSaving(true);
      showLoading();
      await axios.put(`/pays/${id}`, formData);
      showAlert("Abono actualizado correctamente.", "success");
      onButtonClick();
    } catch (error) {
      console.error("Error updating pay:", error);
      showAlert("No se pudo actualizar el abono.", "error");
    } finally {
      setIsSaving(false);
      hideLoading();
    }
  };

  const clientName = payDetail?.lend?.client?.fullname ?? "";
  const clientDocument = payDetail?.lend?.client?.idnumber ?? "";

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} bgcolor="primary.main" color="white">
          <Typography variant="h6">Editar Abono</Typography>
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box p={3} sx={{ overflowY: "auto", flexGrow: 1 }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Typography variant="body1" gutterBottom>
            Abono #{id} {clientName ? `- ${clientName}` : ""}
          </Typography>
          {clientDocument && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Nit: {clientDocument}
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: "grey.100" }}>
                <Typography variant="body2" color="text.secondary">
                  Abono actual
                </Typography>
                <Typography variant="h6">{currencyFormatter.format(originalValue)}</Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  Descuento actual
                </Typography>
                <Typography variant="body1">{currencyFormatter.format(originalDiscount)}</Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  Saldo actual del préstamo
                </Typography>
                <Typography variant="body1">{currencyFormatter.format(currentLoanBalance)}</Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  Saldo base para edición
                </Typography>
                <Typography variant="body1">{currencyFormatter.format(baseBalance)}</Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  Saldo proyectado
                </Typography>
                <Typography variant="h6">{currencyFormatter.format(projectedBalance)}</Typography>

                <Typography variant="body2" color={balanceDifference === 0 ? "text.secondary" : "primary.main"} sx={{ mt: 1 }}>
                  {getBalanceImpactLabel()}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Fecha préstamo: {payDetail?.lend?.date ?? "N/A"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Días desde préstamo: {lendAgeDays ?? "N/A"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Utilidad base descuento: {currencyFormatter.format(utilityValue)}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Forma de Pago</InputLabel>
                <Select value={selectedFormPay} label="Forma de Pago" onChange={handleFormPayChange}>
                  <MenuItem value="1">Efectivo</MenuItem>
                  <MenuItem value="0">Transferencia</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {selectedFormPay === "0" && (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Banco</InputLabel>
                    <Select
                      value={selectedBank}
                      label="Banco"
                      onChange={(event) => setSelectedBank(event.target.value)}
                    >
                      {banks.map((bank) => (
                        <MenuItem key={bank.id} value={String(bank.id)}>
                          {bank.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Número de Recibo"
                    value={receiptNumber}
                    onChange={(event) => setReceiptNumber(event.target.value)}
                  />
                </Grid>
              </>
            )}

            {enableDiscountManual && baseBalance > 0 && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Valor Descuento"
                  type="number"
                  value={discountValue}
                  onChange={handleDiscountValueChange}
                  helperText="Descuento manual: abono + descuento no puede superar el saldo."
                />
              </Grid>
            )}

            {canDiscount && !enableDiscountManual && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={discountEnabled}
                      onChange={(event) => {
                        const nextValue = event.target.checked;
                        setDiscountEnabled(nextValue);
                        if (!nextValue) {
                          setPaymentValue(String(originalValue));
                          setDiscountValue("0");
                        }
                      }}
                    />
                  )}
                  label="Descuento"
                />
                <Typography variant="body2" color="text.secondary">
                  Descuento calculado (50% utilidad): {currencyFormatter.format(Math.max(0, utilityValue * 0.5))}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Valor Abono"
                type="number"
                value={paymentValue}
                onChange={handlePaymentValueChange}
                disabled={!enableDiscountManual && discountEnabled}
              />
            </Grid>

            {!enableDiscountManual && discountEnabled && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Valor Descuento"
                  type="number"
                  value={discountValue}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Cuota actual"
                value={currencyFormatter.format(parseAmount(payDetail?.lend?.daily_pay_value))}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Abono: {currencyFormatter.format(newValue)} | Descuento: {currencyFormatter.format(appliedDiscount)} | Total aplicado: {currencyFormatter.format(newTotal)}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Box display="flex" justifyContent="space-between" p={2} borderTop={1} borderColor="divider">
          <Button variant="outlined" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSaving || !payDetail || Boolean(errorMessage)}
          >
            Guardar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default MyModal;
